import { PortalServiceChangeEventDto, PortalServiceEventDtoMeta } from '@nimbus/portal-service';
import {
  AddBlockEvent,
  AddMenuItemEvent,
  AddVirtualNodeEvent,
  ChangeEvent,
  ChangeEventMeta,
  ChangeEventType,
  ChangeMenuItemVisibilityEvent,
  ChangeMenuItemTypeEvent,
  DeleteBlockEvent,
  DeleteMenuItemEvent,
  DeleteVirtualNodeEvent,
  MoveMenuItemEvent,
  UpdateAccessEvent,
  UpdateBlockEvent,
  UpdateBlockActionsEvent,
  UpdateBlockIndexEvent,
  UpdateBlockPlaceEvent,
  UpdateBlockSizeEvent,
  UpdateBlockVisibilityEvent,
  UpdateBrandingSettingEvent,
  UpdateCustomCodeEvent,
  UpdateMenuItemEvent,
  UpdateMenuItemSlugEvent,
  UpdateMetaSettingsEvent,
  UpdatePortalAnalyticsEvent,
  UpdateSearchAnalyticsEvent,
  UpdateSearchIndexEvent,
  UpdateThemeEvent,
  UpdateHeroSettingEvent,
  UpdatePageEvent,
  ChangePageVisibilityEvent,
  UpdateRequestAccessPageEvent,
  UpdateClientSettingsEvent,
  PushBlockDataEvent,
  UpdateAIAssistantSettingsEvent,
} from '@nx-frontend/shared/types';
import { MenuType, menuItemToDto, menuItemToUnknownMenuItemDto } from '@nx-frontend/shared/types';

export const changeEventMetaToDto = ({ userId, pageId }: ChangeEventMeta): PortalServiceEventDtoMeta => ({
  userId,
  pageId,
});

export const calculateMenuItemPositionType = (menuType: MenuType): 'top' | 'sidebar' | 'footer' | 'unknown' => {
  switch (menuType) {
    case MenuType.PORTAL_SIDEBAR:
      return 'sidebar';
    case MenuType.PORTAL_TOPBAR:
      return 'top';
    case MenuType.PORTAL_FOOTER:
      return 'footer';
    default:
      return 'unknown';
  }
};

const changeEventToDtoFnsMap: Record<
  ChangeEventType,
  (event: ChangeEvent, meta: PortalServiceEventDtoMeta) => PortalServiceChangeEventDto
> = {
  [ChangeEventType.ADD_BLOCK]: (event, meta) => {
    const { type, oldValue, newValue } = <AddBlockEvent>event;
    return { type, meta, oldValue, newValue };
  },
  [ChangeEventType.UPDATE_BLOCK]: (event, meta) => {
    const { type, oldValue, newValue } = <UpdateBlockEvent>event;
    return { type, meta, oldValue, newValue };
  },
  [ChangeEventType.UPDATE_BLOCK_ACTIONS]: (event, meta) => {
    const { type, oldValue, newValue } = <UpdateBlockActionsEvent>event;
    return { type, meta, oldValue, newValue };
  },
  [ChangeEventType.DELETE_BLOCK]: (event, meta) => {
    const { type, oldValue, newValue } = <DeleteBlockEvent>event;
    if (oldValue.type === 'content' || oldValue.type === 'note') {
      return {
        type,
        meta,
        oldValue: { ...oldValue, shareId: oldValue.shareId ?? 0, shareSecurityKey: oldValue.shareSecurityKey ?? '' },
        newValue,
      };
    }
    return { type, meta, oldValue, newValue };
  },
  [ChangeEventType.UPDATE_BLOCK_INDEX]: (event, meta) => {
    const { type, oldValue, newValue } = <UpdateBlockIndexEvent>event;
    return { type, meta, oldValue, newValue };
  },
  [ChangeEventType.UPDATE_BLOCK_PLACE]: (event, meta) => {
    const { type, oldValue, newValue } = <UpdateBlockPlaceEvent>event;
    return { type, meta, oldValue, newValue };
  },
  [ChangeEventType.UPDATE_BLOCK_SIZE]: (event, meta) => {
    const { type, oldValue, newValue } = <UpdateBlockSizeEvent>event;
    return { type, meta, oldValue, newValue };
  },
  [ChangeEventType.UPDATE_BLOCK_VISIBILITY]: (event, meta) => {
    const { type, oldValue, newValue } = <UpdateBlockVisibilityEvent>event;
    return { type, meta, oldValue, newValue };
  },
  [ChangeEventType.PUSH_BLOCK_DATA]: (event, meta) => {
    const { type, oldValue, newValue } = <PushBlockDataEvent>event;
    return { type, meta, oldValue, newValue };
  },
  [ChangeEventType.ADD_MENU_ITEM]: (event, meta) => {
    const { menuType, item } = (<AddMenuItemEvent>event).newValue;
    const positionType = calculateMenuItemPositionType(menuType);
    return {
      type: ChangeEventType.ADD_MENU_ITEM,
      newValue: positionType === 'unknown' ? menuItemToUnknownMenuItemDto(item) : menuItemToDto(item, positionType),
      oldValue: undefined,
      meta,
    };
  },
  [ChangeEventType.DELETE_MENU_ITEM]: (event, meta) => {
    const { menuType, item } = (<DeleteMenuItemEvent>event).oldValue;
    const positionType = calculateMenuItemPositionType(menuType);
    return {
      type: ChangeEventType.DELETE_MENU_ITEM,
      newValue: undefined,
      oldValue: positionType === 'unknown' ? menuItemToUnknownMenuItemDto(item) : menuItemToDto(item, positionType),
      meta,
    };
  },
  [ChangeEventType.UPDATE_MENU_ITEM]: (event, meta) => {
    const { menuType: newMenuType, item: newItem } = (<UpdateMenuItemEvent>event).newValue;
    const newPositionType = calculateMenuItemPositionType(newMenuType);
    const { menuType: oldMenuType, item: oldItem } = (<UpdateMenuItemEvent>event).oldValue;
    const oldPositionType = calculateMenuItemPositionType(oldMenuType);
    return {
      type: ChangeEventType.UPDATE_MENU_ITEM,
      newValue:
        newPositionType === 'unknown' ? menuItemToUnknownMenuItemDto(newItem) : menuItemToDto(newItem, newPositionType),
      oldValue:
        oldPositionType === 'unknown' ? menuItemToUnknownMenuItemDto(oldItem) : menuItemToDto(oldItem, oldPositionType),
      meta,
    };
  },
  [ChangeEventType.CHANGE_MENU_ITEM_VISIBILITY]: (event, meta) => {
    const { item: newItem } = (<ChangeMenuItemVisibilityEvent>event).newValue;
    const { item: oldItem } = (<ChangeMenuItemVisibilityEvent>event).oldValue;

    return {
      type: ChangeEventType.CHANGE_MENU_ITEM_VISIBILITY,
      newValue: newItem,
      oldValue: oldItem,
      meta,
    };
  },
  [ChangeEventType.CHANGE_MENU_ITEM_TYPE]: (event, meta) => {
    const { item: newItem } = (<ChangeMenuItemTypeEvent>event).newValue;
    const { item: oldItem } = (<ChangeMenuItemTypeEvent>event).oldValue;

    return {
      type: ChangeEventType.CHANGE_MENU_ITEM_TYPE,
      newValue: newItem,
      oldValue: oldItem,
      meta,
    };
  },
  [ChangeEventType.UPDATE_MENU_ITEM_SLUG]: (event, meta) => {
    const { item: newItem } = (<UpdateMenuItemSlugEvent>event).newValue;
    const { item: oldItem } = (<UpdateMenuItemSlugEvent>event).oldValue;

    return {
      type: ChangeEventType.UPDATE_MENU_ITEM_SLUG,
      newValue: newItem,
      oldValue: oldItem,
      meta,
    };
  },
  [ChangeEventType.MOVE_MENU_ITEM]: (event, meta) => {
    const { type, oldValue, newValue } = <MoveMenuItemEvent>event;
    const oldPosition = calculateMenuItemPositionType(oldValue.menuType);
    const newPosition = calculateMenuItemPositionType(newValue.menuType);
    return {
      type,
      meta,
      oldValue: {
        id: oldValue.id,
        parentId: oldValue.parentId,
        index: oldValue.index,
        positionType: oldPosition === 'unknown' ? 'sidebar' : oldPosition,
      },
      newValue: {
        id: newValue.id,
        parentId: newValue.parentId,
        index: newValue.index,
        positionType: newPosition === 'unknown' ? 'sidebar' : newPosition,
      },
    };
  },
  [ChangeEventType.ADD_NODE]: (event, meta) => {
    const { id, path, title, visible } = (<AddVirtualNodeEvent>event).newValue;
    return {
      type: 'addPage',
      newValue: {
        id,
        path: path,
        title,
        visible,
      },
      oldValue: undefined,
      meta,
    };
  },
  [ChangeEventType.DELETE_NODE]: (event, meta) => {
    const { id, path, title } = (<DeleteVirtualNodeEvent>event).oldValue;
    return {
      type: 'deletePage',
      oldValue: {
        id,
        path: path,
        title,
        visible: true,
      },
      newValue: undefined,
      meta,
    };
  },
  [ChangeEventType.UPDATE_PAGE]: (event, meta) => {
    const { type, oldValue, newValue } = <UpdatePageEvent>event;
    return { type, meta, oldValue, newValue };
  },
  [ChangeEventType.CHANGE_PAGE_VISIBILITY]: (event, meta) => {
    const { type, oldValue, newValue } = <ChangePageVisibilityEvent>event;
    return { type, meta, oldValue, newValue };
  },
  [ChangeEventType.UPDATE_THEME_SETTINGS]: (event, meta) => {
    const { type, oldValue, newValue } = <UpdateThemeEvent>event;
    return { type, meta, oldValue, newValue };
  },
  [ChangeEventType.UPDATE_SEARCH_ANALYTICS_SETTINGS]: (event, meta) => {
    const { type, oldValue, newValue } = <UpdateSearchAnalyticsEvent>event;
    return { type, meta, oldValue, newValue };
  },
  [ChangeEventType.UPDATE_PORTAL_ANALYTICS_SETTINGS]: (event, meta) => {
    const { type, oldValue, newValue } = <UpdatePortalAnalyticsEvent>event;
    return { type, meta, oldValue, newValue };
  },
  [ChangeEventType.UPDATE_SEARCH_INDEX_SETTINGS]: (event, meta) => {
    const { type, oldValue, newValue } = <UpdateSearchIndexEvent>event;
    return { type, meta, oldValue, newValue };
  },
  [ChangeEventType.UPDATE_ACCESS_SETTINGS]: (event, meta) => {
    const { type, oldValue, newValue } = <UpdateAccessEvent>event;
    return { type, meta, oldValue, newValue };
  },
  [ChangeEventType.UPDATE_CUSTOM_CODE_SETTINGS]: (event, meta) => {
    const { type, oldValue, newValue } = <UpdateCustomCodeEvent>event;
    return { type, meta, oldValue, newValue };
  },
  [ChangeEventType.UPDATE_META_SETTINGS]: (event, meta) => {
    const { type, oldValue, newValue } = <UpdateMetaSettingsEvent>event;
    return { type, meta, oldValue, newValue };
  },
  [ChangeEventType.UPDATE_BRANDING_SETTING]: (event, meta) => {
    const { type, oldValue, newValue } = <UpdateBrandingSettingEvent>event;
    return { type, meta, oldValue, newValue };
  },
  [ChangeEventType.UPDATE_HERO_SETTING]: (event, meta) => {
    const { type, oldValue, newValue } = <UpdateHeroSettingEvent>event;
    return { type, meta, oldValue, newValue };
  },
  [ChangeEventType.UPDATE_REQUEST_ACCESS_PAGE]: (event, meta) => {
    const { type, oldValue, newValue } = <UpdateRequestAccessPageEvent>event;
    return { type, meta, oldValue, newValue };
  },
  [ChangeEventType.UPDATE_CLIENT_SETTINGS]: (event, meta) => {
    const { type, oldValue, newValue } = <UpdateClientSettingsEvent>event;
    return { type, meta, oldValue, newValue };
  },
  [ChangeEventType.UPDATE_AI_ASSISTANT_SETTINGS]: (event, meta) => {
    const { type, oldValue, newValue } = <UpdateAIAssistantSettingsEvent>event;
    return { type, meta, oldValue, newValue };
  },
};

function changeEventToDto(event: ChangeEvent): PortalServiceChangeEventDto {
  const { type, meta } = event;

  const handler = changeEventToDtoFnsMap[type];
  if (handler) {
    return handler(event, changeEventMetaToDto(meta));
  }

  throw new Error(`Unsupported event type ${type}`);
}

export function changeEventsToDto(events: ChangeEvent[]): PortalServiceChangeEventDto[] {
  return events.map((event) => changeEventToDto(event));
}

export const getById =
  <T extends { id: string | undefined }>(id: string) =>
  (item: T): boolean =>
    item.id === id;
